// Convergence tab — iteration-over-iteration charts of max and mean
// uncertainty per output, oracle-mode RMSE & calibration, progress bars
// against per-output target sigma, and a small footer with cumulative cost,
// estimated completion, and autonomous vs manual run breakdown.
//
// Uses Recharts (already a dependency in thread-web) for line and bar charts
// to mirror the style of the settled-methodology article charts. The
// calibration scatter is a small inline SVG until W8's CalibrationScatter
// primitive is available — once W8 lands, swap to
//   import { CalibrationScatter } from '../primitives';

import { useMemo } from 'react';
import {
  Badge,
  Box,
  Flex,
  Grid,
  Heading,
  Progress,
  Separator,
  Text,
} from '@radix-ui/themes';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import type { CampaignState } from '../types';
import { useCampaignStateQuery } from '../api';

const OUTPUT_COLORS = [
  '#4fc3f7', // CL
  '#ffb74d', // CD
  '#81c784', // CY
  '#ba68c8', // Cmx
  '#f06292', // Cmy
  '#ffd54f', // Cmz
  '#64b5f6',
  '#aed581',
];

type ProgressColor = 'green' | 'orange';

export type ConvergenceTabProps = {
  campaignId: string;
};

export function ConvergenceTab({ campaignId }: ConvergenceTabProps) {
  const stateQuery = useCampaignStateQuery(campaignId);
  const state = stateQuery.data;

  if (!state) {
    return (
      <Box p="4">
        <Text color="gray" size="2">
          Loading campaign state...
        </Text>
      </Box>
    );
  }

  return (
    <Box p="4" style={{ overflowY: 'auto' }}>
      <ConvergenceCharts state={state} />
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Inner content
// ---------------------------------------------------------------------------

function ConvergenceCharts({ state }: { state: CampaignState }) {
  const outputs = state.config.outputs;
  const perOutput = state.metrics.perOutput;

  const oracleEnabled =
    state.config.oracle.enabled &&
    outputs.some((o) => (perOutput[o.name]?.realRmseByIter?.length ?? 0) > 0);

  const maxRows = buildIterationRows(state, 'maxSigmaByIter');
  const meanRows = buildIterationRows(state, 'meanSigmaByIter');
  const rmseRows = buildIterationRows(state, 'realRmseByIter');

  const completionLabel = formatIso(state.metrics.estimatedCompletionIso);

  const runBreakdownData = [
    {
      name: 'Runs',
      Autonomous: state.metrics.autonomousRunCount,
      Manual: state.metrics.manualRunCount,
    },
  ];

  return (
    <Flex direction="column" gap="5">
      {/* Progress-bars row */}
      <Box>
        <Heading size="3" mb="2">
          Target uncertainty
        </Heading>
        <Grid columns={{ initial: '1', sm: '2', md: '3' }} gap="3">
          {outputs.map((o, i) => {
            const pm = perOutput[o.name];
            if (!pm || pm.targetSigma === undefined) return null;
            const current = pm.currentMaxSigma;
            const pct = Math.min(
              100,
              Math.max(0, (current / pm.targetSigma) * 100),
            );
            const hit = current <= pm.targetSigma;
            const progressColor: ProgressColor = hit ? 'green' : 'orange';
            return (
              <Box
                key={o.name}
                style={{
                  padding: '10px 12px',
                  borderRadius: 8,
                  background: 'var(--gray-a3)',
                  border: '1px solid var(--gray-a5)',
                }}
              >
                <Flex justify="between" align="center" mb="1">
                  <Flex gap="2" align="center">
                    <ColorDot color={OUTPUT_COLORS[i % OUTPUT_COLORS.length] ?? '#ccc'} />
                    <Text size="2" weight="medium">
                      {o.name}
                    </Text>
                  </Flex>
                  <Badge color={progressColor} variant="soft">
                    {current.toExponential(2)} / {pm.targetSigma.toExponential(2)}
                  </Badge>
                </Flex>
                <Progress value={pct} color={progressColor} size="2" />
              </Box>
            );
          })}
        </Grid>
      </Box>

      <Separator size="4" />

      {/* Max sigma chart */}
      <Box>
        <Heading size="3" mb="2">
          Max uncertainty per iteration
        </Heading>
        <ChartFrame>
          <ResponsiveContainer width="100%" height={260}>
            <LineChart
              data={maxRows}
              margin={{ top: 8, right: 24, bottom: 8, left: 4 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
              <XAxis
                dataKey="iter"
                stroke="#aaa"
                tick={{ fill: '#aaa', fontSize: 12 }}
                label={{
                  value: 'iteration',
                  position: 'insideBottom',
                  offset: -4,
                  fill: '#aaa',
                  fontSize: 12,
                }}
              />
              <YAxis
                stroke="#aaa"
                tick={{ fill: '#aaa', fontSize: 12 }}
                label={{
                  value: 'max sigma',
                  angle: -90,
                  position: 'insideLeft',
                  fill: '#aaa',
                  fontSize: 12,
                }}
              />
              <Tooltip
                contentStyle={{
                  background: '#111316',
                  border: '1px solid #333',
                  color: '#eee',
                }}
              />
              <Legend wrapperStyle={{ color: '#ddd', fontSize: 12 }} />
              {outputs.map((o, i) => (
                <Line
                  key={o.name}
                  type="monotone"
                  dataKey={o.name}
                  stroke={OUTPUT_COLORS[i % OUTPUT_COLORS.length] ?? '#ccc'}
                  dot={false}
                  strokeWidth={2}
                  isAnimationActive={false}
                />
              ))}
              {outputs.map((o, i) => {
                const ts = perOutput[o.name]?.targetSigma;
                if (ts === undefined) return null;
                return (
                  <ReferenceLine
                    key={`ref-${o.name}`}
                    y={ts}
                    stroke={OUTPUT_COLORS[i % OUTPUT_COLORS.length] ?? '#ccc'}
                    strokeDasharray="4 4"
                    strokeOpacity={0.6}
                  />
                );
              })}
            </LineChart>
          </ResponsiveContainer>
        </ChartFrame>
      </Box>

      {/* Mean sigma chart */}
      <Box>
        <Heading size="3" mb="2">
          Mean uncertainty per iteration
        </Heading>
        <ChartFrame>
          <ResponsiveContainer width="100%" height={260}>
            <LineChart
              data={meanRows}
              margin={{ top: 8, right: 24, bottom: 8, left: 4 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
              <XAxis
                dataKey="iter"
                stroke="#aaa"
                tick={{ fill: '#aaa', fontSize: 12 }}
              />
              <YAxis stroke="#aaa" tick={{ fill: '#aaa', fontSize: 12 }} />
              <Tooltip
                contentStyle={{
                  background: '#111316',
                  border: '1px solid #333',
                  color: '#eee',
                }}
              />
              <Legend wrapperStyle={{ color: '#ddd', fontSize: 12 }} />
              {outputs.map((o, i) => (
                <Line
                  key={o.name}
                  type="monotone"
                  dataKey={o.name}
                  stroke={OUTPUT_COLORS[i % OUTPUT_COLORS.length] ?? '#ccc'}
                  dot={false}
                  strokeWidth={2}
                  isAnimationActive={false}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </ChartFrame>
      </Box>

      {/* Oracle-mode extras */}
      {oracleEnabled && (
        <>
          <Separator size="4" />
          <Box>
            <Heading size="3" mb="2">
              Real RMSE vs oracle per iteration
            </Heading>
            <ChartFrame>
              <ResponsiveContainer width="100%" height={260}>
                <LineChart
                  data={rmseRows}
                  margin={{ top: 8, right: 24, bottom: 8, left: 4 }}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="rgba(255,255,255,0.08)"
                  />
                  <XAxis
                    dataKey="iter"
                    stroke="#aaa"
                    tick={{ fill: '#aaa', fontSize: 12 }}
                  />
                  <YAxis stroke="#aaa" tick={{ fill: '#aaa', fontSize: 12 }} />
                  <Tooltip
                    contentStyle={{
                      background: '#111316',
                      border: '1px solid #333',
                      color: '#eee',
                    }}
                  />
                  <Legend wrapperStyle={{ color: '#ddd', fontSize: 12 }} />
                  {outputs.map((o, i) => (
                    <Line
                      key={o.name}
                      type="monotone"
                      dataKey={o.name}
                      stroke={OUTPUT_COLORS[i % OUTPUT_COLORS.length] ?? '#ccc'}
                      dot={false}
                      strokeWidth={2}
                      isAnimationActive={false}
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </ChartFrame>
          </Box>

          <Box>
            <Heading size="3" mb="2">
              Calibration: predicted sigma vs realised absolute error
            </Heading>
            <Grid columns={{ initial: '1', sm: '2', md: '3' }} gap="3">
              {outputs.map((o, i) => {
                const points = perOutput[o.name]?.calibration ?? [];
                return (
                  <Box
                    key={o.name}
                    style={{
                      padding: 8,
                      borderRadius: 8,
                      border: '1px solid var(--gray-a5)',
                      background: '#111316',
                    }}
                  >
                    <Text size="2" weight="medium" mb="1">
                      {o.name}
                    </Text>
                    <FallbackCalibration
                      points={points}
                      color={OUTPUT_COLORS[i % OUTPUT_COLORS.length] ?? '#ccc'}
                    />
                  </Box>
                );
              })}
            </Grid>
          </Box>
        </>
      )}

      <Separator size="4" />

      {/* Footer stats */}
      <Grid columns={{ initial: '1', sm: '3' }} gap="4">
        <StatBlock
          label="Cumulative cost"
          value={`${state.metrics.cumulativeCostFlexCredits.toFixed(1)} FC`}
        />
        <StatBlock
          label="Estimated completion"
          value={completionLabel ?? '—'}
        />
        <Box>
          <Text size="1" color="gray">
            Autonomous vs manual
          </Text>
          <Box style={{ height: 60, marginTop: 4 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={runBreakdownData}
                layout="vertical"
                margin={{ top: 4, right: 12, bottom: 4, left: 12 }}
              >
                <XAxis type="number" hide />
                <YAxis type="category" dataKey="name" hide />
                <Tooltip
                  contentStyle={{
                    background: '#111316',
                    border: '1px solid #333',
                    color: '#eee',
                  }}
                />
                <Bar dataKey="Autonomous" stackId="a" fill="#4fc3f7" />
                <Bar dataKey="Manual" stackId="a" fill="#ffb74d" />
              </BarChart>
            </ResponsiveContainer>
          </Box>
          <Text size="1" color="gray">
            {state.metrics.autonomousRunCount} autonomous ·{' '}
            {state.metrics.manualRunCount} manual
          </Text>
        </Box>
      </Grid>
    </Flex>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function buildIterationRows(
  state: CampaignState,
  key: 'maxSigmaByIter' | 'meanSigmaByIter' | 'realRmseByIter',
): Array<Record<string, number | string>> {
  const outputs = state.config.outputs;
  let nIter = 0;
  for (const o of outputs) {
    const series = state.metrics.perOutput[o.name]?.[key] ?? [];
    if (series.length > nIter) nIter = series.length;
  }
  const rows: Array<Record<string, number | string>> = [];
  for (let i = 0; i < nIter; i++) {
    const row: Record<string, number | string> = { iter: i + 1 };
    for (const o of outputs) {
      const series = state.metrics.perOutput[o.name]?.[key] ?? [];
      const v = series[i];
      if (v !== undefined) {
        row[o.name] = v;
      }
    }
    rows.push(row);
  }
  return rows;
}

function formatIso(iso?: string): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function ChartFrame({ children }: { children: React.ReactNode }) {
  return (
    <Box
      style={{
        background: '#111316',
        border: '1px solid var(--gray-a5)',
        borderRadius: 8,
        padding: 8,
      }}
    >
      {children}
    </Box>
  );
}

function ColorDot({ color }: { color: string }) {
  return (
    <Box
      style={{
        width: 10,
        height: 10,
        borderRadius: 2,
        background: color,
      }}
    />
  );
}

function StatBlock({ label, value }: { label: string; value: string }) {
  return (
    <Box>
      <Text size="1" color="gray">
        {label}
      </Text>
      <Text size="4" weight="bold" style={{ display: 'block' }}>
        {value}
      </Text>
    </Box>
  );
}

// Tiny inline scatter used until W8 ships CalibrationScatter.
function FallbackCalibration({
  points,
  color,
}: {
  points: Array<{ predictedSigma: number; realisedAbsError: number }>;
  color: string;
}) {
  const { vbMin, vbMax } = useMemo(() => {
    let vMax = 0;
    for (const p of points) {
      if (p.predictedSigma > vMax) vMax = p.predictedSigma;
      if (p.realisedAbsError > vMax) vMax = p.realisedAbsError;
    }
    if (vMax === 0) vMax = 1;
    return { vbMin: 0, vbMax: vMax * 1.05 };
  }, [points]);

  const size = 140;
  const pad = 4;

  const project = (v: number) =>
    pad + ((v - vbMin) / (vbMax - vbMin || 1)) * (size - 2 * pad);

  return (
    <svg
      width="100%"
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      style={{ display: 'block' }}
    >
      <line
        x1={project(vbMin)}
        y1={size - project(vbMin)}
        x2={project(vbMax)}
        y2={size - project(vbMax)}
        stroke="rgba(255,255,255,0.25)"
        strokeDasharray="3 3"
      />
      {points.map((p, i) => (
        <circle
          key={i}
          cx={project(p.predictedSigma)}
          cy={size - project(p.realisedAbsError)}
          r={2.5}
          fill={color}
          fillOpacity={0.8}
        />
      ))}
    </svg>
  );
}

export default ConvergenceTab;
